// Modules
import axios, { AxiosResponse } from "axios"

// Constants
import config from "~constants/config"
import { Endpoints } from "~constants/endpoints"

// Utils
import { Api } from "~utils/api"
import {
  httpErrorInterceptor,
  httpSuccessInterceptor,
} from "~utils/httpInterceptors"
import { createAxiosInstance } from "~utils/axios"

export interface AuthenticatePayload {
  username: string
  password: string
}

export interface RefreshTokenPayload {
  refreshToken: string
}

let _sessionPromise: Promise<any> | null = null

const AuthService = {
  getCompanyFromInfoUrl: async (userId: string) => {
    return await Api.get(Endpoints.USER_COMPANY.URL.replace(":userId", userId))
  },
  postAuthenticate: async (payload: AuthenticatePayload): Promise<any> => {
    const formData = new FormData()
    formData.append("username", payload.username)
    formData.append("password", payload.password)
    formData.append("grant_type", "password")

    const instance = createAxiosInstance()

    // Define interceptors
    instance.interceptors.response.use(
      httpSuccessInterceptor,
      httpErrorInterceptor,
    )

    try {
      const response = await instance.post(Endpoints.AUTH.URL, formData)
      return response.data
    } catch (error) {
      return Promise.reject(error)
    }
  },
  postRefreshToken: async (payload: RefreshTokenPayload): Promise<any> => {
    const formData = new FormData()
    formData.append("refresh_token", payload.refreshToken)
    formData.append("grant_type", "refresh_token")

    const instance = createAxiosInstance()

    try {
      const response = await instance.post(Endpoints.AUTH.URL, formData)
      return response.data
    } catch (error) {
      return Promise.reject(error)
    }
  },
  getResetPassword: async (username: string) => {
    const url = Endpoints.RESET_PASSWORD.URL.replace(
      ":username",
      encodeURIComponent(username),
    )

    const instance = createAxiosInstance()

    try {
      return await instance.get(url)
    } catch (err) {
      return Promise.reject(err)
    }
  },
  getResetAuthCode: async (username: string, code: string) => {
    const data = {
      username: username,
      challenge_code: code,
      disable_redirect: "true",
      scope: "reset_password",
      response_type: "code",
      client_id: config.authClientId,
      grant_type: "authorization_code",
    }

    const queryStrings = new URLSearchParams(data).toString()

    try {
      // Public OAuth endpoint: the user is authenticated via the emailed
      // challenge_code, so we must NOT send a client Basic-auth header here.
      // (createAxiosInstance() injects Basic clientId:clientSecret, which the
      // gateway rejects with 401 on this /public route.)
      const response = await axios.get(
        `${Endpoints.RESET_AUTH_CODE.URL}?${queryStrings}`,
        { headers: { Accept: "application/json" } },
      )
      return response.data
    } catch (error) {
      return Promise.reject(error)
    }
  },
  getAccessTokenResetPassword: async (code: string) => {
    const formData = new FormData()
    formData.append("code", code)
    formData.append("grant_type", "authorization_code")
    formData.append("scope", "reset_password")

    const instance = createAxiosInstance()
    try {
      const response = await instance.post(Endpoints.AUTH.URL, formData)
      return response.data
    } catch (error) {
      return Promise.reject(error)
    }
  },
  postLogout: async () => await Api.delete(Endpoints.LOGOUT.URL),

  // ── SPA BFF auth (HttpOnly cookie flow) ──────────────────────────────

  spaLogin: async (payload: AuthenticatePayload): Promise<any> => {
    const res = await fetch(Endpoints.SPA_LOGIN.URL, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    })
    if (!res.ok) {
      const body = await res.json().catch(() => ({}))
      return Promise.reject(new Error(body?.message || "Login failed"))
    }
    return res.json()
  },

  spaRefresh: async (): Promise<any> => {
    const res = await fetch(Endpoints.SPA_REFRESH.URL, {
      method: "POST",
      credentials: "include",
    })
    if (!res.ok) return Promise.reject(new Error("Session expired"))
    return res.json()
  },

  spaSession: (): Promise<any> => {
    if (!_sessionPromise) {
      _sessionPromise = fetch(Endpoints.SPA_SESSION.URL, {
        credentials: "include",
      })
        .then(res => {
          if (!res.ok) return null
          return res.json()
        })
        .finally(() => {
          _sessionPromise = null
        })
    }
    return _sessionPromise
  },

  spaLogout: async (): Promise<void> => {
    await fetch(Endpoints.SPA_LOGOUT.URL, {
      method: "POST",
      credentials: "include",
    }).catch(() => {})
  },
}

export default AuthService
